import { apiFetch } from './apiClient';
import { comparisonCache } from './comparisonCache';

export interface CompetitorData { price: string; stock: string; link: string; scrapping_id: number; }

/**
 * A competitor link of a product.
 *
 * `status` is the scrapper-list row state: 'a' = active, 'w' = waiting for
 * review, 'i' = inactive. `hasPrice` tells the client whether the latest
 * scrape of that link produced a price - a link without a price must never be
 * offered as a clickable competitor link.
 */
export interface ScrapperLink {
  id: number;
  title: string;
  url: string;
  status: string;
  hasPrice: boolean;
}

export interface ComparisonRow {
  id: number;
  date: string;
  title: string;
  brand?: string;
  category?: string;
  sku?: string;
  last_selling_date?: string;
  last_selling_price?: string;
  lastSyncedAt?: string | null;
  price: number | string;
  stock: number | string;
  link?: string;
  offer_price?: number | string;
  scrapping_id?: number;
  /**
   * Cost Master (DP+ / NLC+ / NLC All), mirrored from o4_mdcomputers.cost_master
   * by scripts/sync-md.js. All optional so the dashboard keeps working against a
   * backend that has not been redeployed yet — the Cost Master section then
   * falls back to its "no data" state.
   */
  cost_dp?: number | null;
  cost_nlc?: number | null;
  cost_nlc_all?: number | null;
  cost_busy_stock?: number | null;
  cost_updated_at?: string | null;
  competitors: Record<string, CompetitorData>;
}

export interface ComparisonQuery {
  filters: { category?: string; brand?: string; search?: string };
  inStockOnly: boolean;
  priceFilter: string;
  company: string;
  daysBack: number;
  skip: number;
  limit: number;
  refresh?: boolean;
}

/** The applied filters of a comparison view — no paging. */
export interface ComparisonFilterParams {
  filters: { category?: string; brand?: string; search?: string };
  inStockOnly: boolean;
  priceFilter: string;
  company: string;
  daysBack: number;
}

export interface SummaryResponse {
  pieChart: { pieVal: [number, number, number] };
  /** Brand -> item count. Sent by the current (pruned) backend response. */
  brands?: Record<string, number>;
  /**
   * Legacy container still served by the backend deployed before the summary
   * response was pruned - it carried the same brand counts. Frontend and
   * backend are deployed independently, so both shapes must stay readable.
   */
  horizotalChartBar?: {
    horizontalXVal?: string[];
    horizontalYVal?: number[];
    colors?: string[];
    brands?: Record<string, number>;
  };
  mdVsMarket?: { mdAvg: number; marketAvg: number; pct: number | null } | null;
}

export const dateFromDaysBack = (daysBack: number): string => {
  // Crawler stores scrape timestamps in IST (UTC+5:30). Shift now → IST,
  // then take the UTC date of the shifted instant = IST calendar date.
  const d = new Date(Date.now() + 5.5 * 3600 * 1000);
  d.setUTCDate(d.getUTCDate() - daysBack);
  return d.toISOString().slice(0, 10); // 'YYYY-MM-DD' (IST)
};

/**
 * The filter params every comparison request shares.
 *
 * The table, the summary and the CSV export all send exactly these params —
 * one builder, so the export can never drift away from the table.
 */
const buildFilterParams = (q: ComparisonFilterParams): URLSearchParams => {
  const params = new URLSearchParams();
  if (q.filters.category) params.set('category', q.filters.category);
  if (q.filters.brand) params.set('brand', q.filters.brand);
  if (q.filters.search) params.set('search', q.filters.search);
  if (q.inStockOnly) params.set('inStockCheck', 'true');
  params.set('price', q.priceFilter);
  params.set('company', q.company);
  params.set('date', dateFromDaysBack(q.daysBack));
  return params;
};

const buildQuery = (q: ComparisonQuery): string => {
  const params = buildFilterParams(q);
  params.set('skip', String(q.skip));
  params.set('limit', String(q.limit));
  return params.toString();
};

const buildSummaryQuery = (q: ComparisonQuery): string =>
  buildFilterParams(q).toString();

/**
 * Query params for the CSV export — the same filters the table uses, but
 * WITHOUT paging: the export carries the whole filtered result set.
 */
const buildExportQuery = (q: ComparisonFilterParams, productId?: number): string => {
  const params = buildFilterParams(q);
  if (productId) params.set('product_id', String(productId));
  return params.toString();
};

/** The file name the server asked for, if it sent one. */
const filenameFromDisposition = (disposition: string | null): string | null => {
  if (!disposition) return null;
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return match ? match[1] : null;
};

/** A downloaded CSV plus what the server said about it. */
export interface CsvExportResult {
  blob: Blob;
  /** Rows written to the file. */
  exportedRows: number | null;
  /** Rows matching the filter — larger than `exportedRows` when the cap hit. */
  totalRows: number | null;
  /** The server's file name, when it sent one. */
  filename: string | null;
}

export const comparisonService = (token: string | null) => ({
  competitors: () =>
    apiFetch<{ competitors: string[] }>(token, '/api/comparison/competitors'),
  list: async (q: ComparisonQuery) => {
    const url = `/api/comparison?${buildQuery(q)}`;

    // Today → always network; &ts= only on explicit refresh; never cache
    if (q.daysBack === 0) {
      const finalUrl = q.refresh ? `${url}&ts=${Date.now()}` : url;
      return apiFetch<ComparisonRow[]>(token, finalUrl);
    }

    // Historical → pagination appends bypass cache
    if (q.skip > 0) {
      return apiFetch<ComparisonRow[]>(token, url);
    }

    // First page: cache hit → instant
    const cached = comparisonCache.getCache(url);
    if (cached) {
      return Promise.resolve(cached as ComparisonRow[]);
    }

    // Cache miss → fetch + store
    const data = await apiFetch<ComparisonRow[]>(token, url);
    comparisonCache.setCache(url, data);
    return data;
  },
  summary: async (q: ComparisonQuery) => {
    const url = `/api/comparison/summary?${buildSummaryQuery(q)}`;

    // Today → always network unless explicit refresh; never cache
    if (q.daysBack === 0) {
      const finalUrl = q.refresh ? `${url}&ts=${Date.now()}` : url;
      return apiFetch<SummaryResponse>(token, finalUrl);
    }

    // Historical → cache hit → instant
    const cached = comparisonCache.getCache(url);
    if (cached) {
      return Promise.resolve(cached as SummaryResponse);
    }

    // Cache miss → fetch + store
    const data = await apiFetch<SummaryResponse>(token, url);
    comparisonCache.setCache(url, data);
    return data;
  },
  single: (productId: number, daysBack?: number, signal?: AbortSignal) => {
    const params = new URLSearchParams({ product_id: String(productId) });
    if (daysBack) params.set('daysBack', String(daysBack));
    return apiFetch<Record<string, unknown>[]>(token, `/api/comparison/single?${params}`, { signal });
  },
  /**
   * Download the comparison table as CSV.
   *
   * The backend builds the file in memory and returns it in one response
   * (text/csv), so the response body IS the CSV file — fetched as a Blob to
   * keep the bytes (and the UTF-8 BOM) intact. The response headers carry the
   * row counts, so a truncated export can be reported instead of silently
   * looking complete.
   * `productId` narrows the export to a single product.
   */
  exportCsv: async (q: ComparisonFilterParams, productId?: number): Promise<CsvExportResult> => {
    const url = `/api/comparison/export.csv?${buildExportQuery(q, productId)}`;
    const res = await apiFetch<Response>(token, url, {}, 'raw');
    const blob = await res.blob();
    const num = (name: string): number | null => {
      const raw = res.headers.get(name);
      if (raw === null) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    };
    return {
      blob,
      exportedRows: num('x-exported-rows'),
      totalRows: num('x-total-rows'),
      filename: filenameFromDisposition(res.headers.get('content-disposition')),
    };
  },
  scrapperLinks: (productId: number) =>
    apiFetch<{ links: ScrapperLink[] }>(
      token, `/api/comparison/scrapper-links?product_id=${productId}`,
    ),
  upsertLink: (payload: { prod_id: number; title: string; url: string }) =>
    apiFetch<{ ok: true; id: number; action: 'created' | 'updated' }>(
      token, '/api/comparison/scrapper-link', {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    ),
  categories: () =>
    apiFetch<{ categories: string[] }>(token, '/api/products/categories'),
  brands: () =>
    apiFetch<{ brands: string[] }>(token, '/api/products/brands'),
});
